import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { MdRemoveCircle } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";

import { useDispatch, useSelector } from "react-redux";
import { addProductToCart, updateCartItemQuantity, removeProductFromCart } from "../redux/cartSlice";
import { addProductToWishlist, removeProductFromWishlist } from "../redux/WishlistSlice";
import { Link } from "react-router-dom";
import { getFallbackImage, loadImageWithCache } from "../utils/imageLoader";
import "./ProductModal.css";
import { toast } from 'react-hot-toast';

const styles = {
  quantityContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textAlign: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  quantityButton: {
    border: "none",
    backgroundColor: "transparent",
    color: "#82278A",
    fontSize: "30px",
    fontWeight: "800",
    width: "34px",
    height: "24px",
    borderRadius: "20%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    marginBottom: "15px",
  },
  quantity: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#82278A",
    minWidth: "20px",
    textAlign: "center",
    marginBottom: "15px",
  },
};

const ProductModal = ({ post, isOpen, onClose, initialSelectedColor = '', initialSelectedSize = '' }) => {
  const dispatch = useDispatch();
  const wishlistItems = useSelector(state => state.wishlist.items);
  const cartItems = useSelector(state => state.cart.items);

  const [selectedColor, setSelectedColor] = useState(initialSelectedColor);
  const [selectedSize, setSelectedSize] = useState(initialSelectedSize);
  const [loadedImages, setLoadedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const imageContainerRef = useRef(null);
  const lastThumbnailClickRef = useRef(0);
  const [mainImageSrc, setMainImageSrc] = useState('');

  const isInWishlist = post ? wishlistItems.some(item => item.id === post._id) : false;
  const currentItem = post ? cartItems.find(item => item.id === post._id) : null;
  const quantity = currentItem ? currentItem.quantity : 0;

  // FIXED: Properly extract image URLs from API response
  const extractImageUrls = (imagesArray) => {
    if (!imagesArray || !Array.isArray(imagesArray)) return [];
    
    return imagesArray.map(img => {
      // Handle both string URLs and object format from API
      if (typeof img === 'string') return img;
      if (img && img.url) return img.url;
      if (img && img.public_id) {
        // Construct URL from Cloudinary public_id if needed
        return `https://res.cloudinary.com/dytcxexpb/image/upload/${img.public_id}`;
      }
      return getFallbackImage();
    }).filter(url => url && url !== getFallbackImage());
  };

  const images = useMemo(() => extractImageUrls(post?.images), [post?.images]);
  const uniqueColors = post?.variants ? [...new Set(post.variants.map(v => v.color).filter(Boolean))] : [];
  const uniqueSizes = post?.variants ? [...new Set(post.variants.map(v => v.size).filter(Boolean))] : [];
  const selectedVariant = post?.variants?.find(v => v.color === selectedColor && v.size === selectedSize) || post?.variants?.[0];
  const price = selectedVariant?.price || 0;

  useEffect(() => {
    if (isOpen && post) {
      if (initialSelectedColor) setSelectedColor(initialSelectedColor);
      if (initialSelectedSize) setSelectedSize(initialSelectedSize);

      if ((!initialSelectedColor || !initialSelectedSize) && post?.variants?.length) {
        const first = post.variants[0];
        setSelectedColor(prev => prev || first.color || '');
        setSelectedSize(prev => prev || first.size || '');
      }
    }
  }, [isOpen, post, initialSelectedColor, initialSelectedSize]);

  useEffect(() => {
    const loadImages = async () => {
      if (images.length > 0) {
        const loaded = await Promise.all(images.map(img => loadImageWithCache(img)));
        setLoadedImages(loaded);
      } else {
        setLoadedImages([]);
      }
      setCurrentImageIndex(0);
    };
    loadImages();
  }, [post, images]);

  // Keep a separate main image src for immediate rendering on thumbnail click
  useEffect(() => {
    if (loadedImages && loadedImages.length > 0) {
      const src = loadedImages[currentImageIndex] || loadedImages[0];
      setMainImageSrc(src);
    } else if (images && images.length > 0) {
      setMainImageSrc(images[0]);
    } else {
      setMainImageSrc('');
    }
  }, [loadedImages, currentImageIndex, images]);

  useEffect(() => {
    if (imageContainerRef.current && loadedImages.length > 0) {
      const containerWidth = imageContainerRef.current.clientWidth;
      imageContainerRef.current.scrollTo({
        left: currentImageIndex * containerWidth,
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex, loadedImages]);

  // Enhanced Touch Handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragStartTime(Date.now());
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'none';
      imageContainerRef.current.style.scrollBehavior = 'auto';
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !imageContainerRef.current) return;
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diffX = currentX - dragStartX;
    
    imageContainerRef.current.scrollLeft -= diffX * 0.8;
    setDragStartX(currentX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'x mandatory';
      imageContainerRef.current.style.scrollBehavior = 'smooth';
      
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      
      setCurrentImageIndex(Math.max(0, Math.min(newIndex, images.length - 1)));
      imageContainerRef.current.scrollTo({ 
        left: newIndex * containerWidth, 
        behavior: 'smooth' 
      });
    }
  };

  // Mouse Handlers
  const handleMouseDown = (e) => {
    if (e.touches) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    if (imageContainerRef.current) imageContainerRef.current.style.scrollSnapType = 'none';
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !imageContainerRef.current || e.touches) return;
    const currentX = e.clientX;
    const offset = currentX - dragStartX;
    imageContainerRef.current.scrollLeft -= offset;
    setDragStartX(currentX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'x mandatory';
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      setCurrentImageIndex(Math.max(0, Math.min(newIndex, images.length - 1)));
      imageContainerRef.current.scrollTo({ left: Math.round(newIndex * containerWidth), behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (imageContainerRef.current) {
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      if (newIndex !== currentImageIndex && newIndex >= 0 && newIndex < images.length) {
        setCurrentImageIndex(newIndex);
      }
    }
  };

  // Thumbnail click handler
  const handleThumbnailClick = useCallback((index) => {
    const now = Date.now();
    if (now - (lastThumbnailClickRef.current || 0) < 200) return;
    lastThumbnailClickRef.current = now;

    setCurrentImageIndex(index);
    // update main image src immediately for reliable UI change
    if (loadedImages && loadedImages[index]) {
      setMainImageSrc(loadedImages[index]);
    } else if (images && images[index]) {
      setMainImageSrc(images[index]);
    }
    // Keep scroll adjustment
    try {
      if (imageContainerRef.current) {
        const containerWidth = imageContainerRef.current.clientWidth || 1;
        imageContainerRef.current.scrollTo({ 
          left: Math.round(index * containerWidth), 
          behavior: 'smooth' 
        });
      }
    } catch (e) {
    }
  }, [images, loadedImages]);

  const handleAddToCart = () => {
    if (!post?.variants?.length) return;

    const matchingVariant = post.variants.find(v => v.color === selectedColor && v.size === selectedSize);

    if (!matchingVariant) {
      toast.error("Please select a valid color and size combination.");
      return;
    }

    const itemToAdd = {
      ...post,
      id: post._id,
      price: matchingVariant.price,
      image: images[0] || getFallbackImage(),
      title: post.name,
      variantId: matchingVariant._id,
      selectedColor,
      selectedSize
    };
    dispatch(addProductToCart(itemToAdd, 1));
    toast.success("Added to cart");
  };

  const handleIncrement = () => {
    if (currentItem) {
      dispatch(updateCartItemQuantity(currentItem, quantity + 1));
    }
  };

  const handleDecrement = () => {
    if (currentItem) {
      if (quantity === 1) {
        dispatch(removeProductFromCart(currentItem));
      } else {
        dispatch(updateCartItemQuantity(currentItem, quantity - 1));
      }
    }
  };

  const toggleWishlist = () => {
    const safeImage = images[0] || getFallbackImage();
    const wishlistItemData = {
      ...post,
      id: post._id,
      price,
      image: safeImage,
      title: post.name,
      wishlistId: wishlistItems.find(item => item.id === post._id)?.wishlistId
    };

    if (isInWishlist) {
      dispatch(removeProductFromWishlist(wishlistItemData));
    } else {
      dispatch(addProductToWishlist(wishlistItemData));
    }
  };

  if (!post) return null;

  const imageSize = '400px';

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ border: 'none' }}>
        <Modal.Title>{post.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ border: 'none' }}>
        <div className="modal-main-content">
          
          <div className="image-section-wrapper">
              {/* Main Image Carousel */}
              <div
                ref={imageContainerRef}
                className="image-container"
                style={{ position: 'relative' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onScroll={handleScroll}
              >
                <div className="carousel-images">
                  {mainImageSrc ? (
                    <img
                      key={mainImageSrc}
                      src={mainImageSrc}
                      alt={`${post.name} main`}
                      className="modal-image"
                      style={{
                        flexShrink: 0,
                        width: imageSize,
                        height: imageSize,
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                      onError={(e) => { 
                        console.error("Main image failed to load:", mainImageSrc);
                        e.target.src = getFallbackImage(); 
                      }}
                    />
                  ) : (
                    <img
                      src={getFallbackImage()}
                      alt={`${post.name} fallback`}
                      className="modal-image"
                      style={{ width: imageSize, height: imageSize }}
                    />
                  )}
                </div>
              </div>

              {/* Thumbnails Gallery*/}
              {images.length > 1 && (
                <div className="thumbnails-container">
                  {images.map((img, index) => (
                    <div 
                      key={img || index}
                      className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <img
                        src={img}
                        alt={`${post.name} thumbnail ${index + 1}`}
                        className="thumbnail-image"
                        onError={(e) => { 
                          console.error("Thumbnail failed to load:", img);
                          e.target.src = getFallbackImage(); 
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
          </div>

          <div className="details-section">
            <h5>Description</h5>
            <p>{post.description || "No description available."}</p>
            <h5>Price: ₹{price.toFixed(2)}</h5>

            {uniqueColors.length > 0 && (
              <div>
                <h5>Color</h5>
                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="modal-select"
                >
                  <option value="">Select Color</option>
                  {uniqueColors.map(color => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {uniqueSizes.length > 0 && (
              <div>
                <h5>Size</h5>
                <select
                  value={selectedSize}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  className="modal-select"
                >
                  <option value="">Select Size</option>
                  {uniqueSizes.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px", justifyContent: "center", flexWrap: "wrap" }}>
              {quantity === 0 ? (
                <button className="btn btn-primary" onClick={handleAddToCart} style={{ backgroundColor: "#82278A" }}>
                  Add to Cart
                </button>
              ) : (
                <div style={styles.quantityContainer}>
                  <button style={styles.quantityButton} onClick={handleDecrement} aria-label="Decrease quantity">
                    <MdRemoveCircle />
                  </button>
                  <span style={styles.quantity}>{quantity}</span>
                  <button style={styles.quantityButton} onClick={handleIncrement} aria-label="Increase quantity">
                    <IoIosAddCircle />
                  </button>
                </div>
              )}
              <button
                className={`btn ${isInWishlist ? "btn-danger" : "btn-outline-danger"}`}
                onClick={toggleWishlist}
              >
                {isInWishlist ? <AiFillHeart /> : <AiOutlineHeart />} Wishlist
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ border: 'none' }}>
        <Link to="/products">
          <Button variant="secondary" onClick={onClose} className="btn img-btn p-3" style={{ color: "#fff" }}>
             Shop Now
          </Button>
        </Link>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductModal;